// Service worker registration and the update flow of the installable web app (F61, first stage).
//
// The worker (generated by vite-plugin-pwa, see vite.config.ts) precaches the built shell and nothing else:
// no API response is ever cached by it. This module only registers it, notices that a newer one is waiting
// and, when a person clicks, lets it take over. The page never reloads by itself, because a reload
// would drop what someone is typing.
//
// Registration is production only: the development server serves modules without a precache, and a worker
// there would only hide changes. The script URL comes from the "scopefold" Trusted Types policy because the
// app's CSP refuses a plain string.

import { trustedScriptUrl } from "../trusted-types";

type Listener = () => void;

const listeners = new Set<Listener>();
let waiting: ServiceWorker | undefined;
let registered = false;

export function subscribeToUpdates(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while a newer version of the app is downloaded and waits for a click. */
export function isUpdateReady(): boolean {
  return waiting !== undefined;
}

function updateReady(worker: ServiceWorker): void {
  waiting = worker;
  listeners.forEach((listener) => {
    listener();
  });
}

/** Activates the waiting version and reloads once it controls the page. */
export function applyUpdate(): void {
  if (waiting === undefined) return;
  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => {
      window.location.reload();
    },
    { once: true },
  );
  waiting.postMessage({ type: "SKIP_WAITING" });
}

function watch(registration: ServiceWorkerRegistration): void {
  // A worker that waits while none controls the page is the first install, not an update.
  if (registration.waiting && navigator.serviceWorker.controller) updateReady(registration.waiting);
  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    installing?.addEventListener("statechange", () => {
      if (installing.state === "installed" && navigator.serviceWorker.controller) updateReady(installing);
    });
  });
  // An installed app can stay open for days: look for a new version whenever it comes back to the front.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") registration.update().catch(() => undefined);
  });
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || registered || !("serviceWorker" in navigator)) return;
  registered = true;
  const register = async (): Promise<void> => {
    try {
      // lib.dom types the argument as string or URL; browsers also accept the TrustedScriptURL.
      const registration = await navigator.serviceWorker.register(trustedScriptUrl(`${import.meta.env.BASE_URL}sw.js`) as string, {
        scope: import.meta.env.BASE_URL,
      });
      watch(registration);
    } catch (error) {
      // The app works without a worker; it just is not installable and does not open offline.
      console.warn("Service worker registration failed.", error);
    }
  };
  if (document.readyState === "complete") {
    void register();
  } else {
    window.addEventListener("load", () => void register(), { once: true });
  }
}
